import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Timer } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { clipDurationReviewStatus, formatClipDuration } from '@/lib/campaigns/clipDuration';
import { MinClipDurationContext } from './minClipDurationContext';

/**
 * A clip's measured length for reviewers. With a campaign minimum, an
 * unmeasured clip (the platform did not give its length) is flagged for a
 * manual check, and a clip admitted before the minimum was raised shows as
 * below it.
 */
export function ClipDurationBadge({ durationSeconds, minSeconds }: { durationSeconds?: number | null; minSeconds?: number | null }) {
  const { t } = useTranslation();
  const contextMin = useContext(MinClipDurationContext);
  const min = minSeconds === undefined ? contextMin : minSeconds;
  const status = clipDurationReviewStatus({ minSeconds: min, durationSeconds });
  const duration = formatClipDuration(durationSeconds);
  if (!duration && status !== 'unmeasured') return null;
  const tone = status === 'below' ? 'border-red-500/40 text-red-500'
    : status === 'unmeasured' ? 'border-amber-500/40 text-amber-500' : 'text-muted-foreground';
  const label = status === 'unmeasured' ? t('campaign.clipDuration.unmeasured')
    : status === 'below' ? t('campaign.clipDuration.belowMinimum', { duration, min: formatClipDuration(min) })
      : t('campaign.clipDuration.value', { duration });
  return <Badge variant="outline" data-testid="clip-duration-badge" data-status={status} className={`mt-1 gap-1 whitespace-nowrap text-[10px] ${tone}`}>
    <Timer aria-hidden="true" className="h-3 w-3 shrink-0" />{label}
  </Badge>;
}
